import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plane, MapPin, Plus, Crown, TrendingUp, Zap, BarChart2,
  Route, Star, ArrowRight, Globe, Trophy, CalendarDays, Users,
  Heart, BookOpen
} from 'lucide-react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { BadgeShowcase, RankRoadmap, XPRing, RankBadge } from '../components/GamificationUI';
import ChallengeWidget from '../components/ChallengeWidget';
import QuizWidget from '../components/QuizWidget';

import { API } from '../config';

const RANK_COLORS = {
  'Boarding Pass':  { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', icon: '🎫' },
  'Frequent Flyer': { bg: 'bg-sky-100 dark:bg-sky-900/40',  text: 'text-sky-700 dark:text-sky-400',    icon: '✈️' },
  'Silver Traveler':{ bg: 'bg-gray-100 dark:bg-gray-800',   text: 'text-gray-600 dark:text-gray-300',   icon: '🥈' },
  'Gold Traveler':  { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-400', icon: '🥇' },
  'Platinum Flyer': { bg: 'bg-cyan-100 dark:bg-cyan-900/40',   text: 'text-cyan-700 dark:text-cyan-400',   icon: '💎' },
  'Sky Legend':     { bg: 'bg-violet-100 dark:bg-violet-900/40', text: 'text-violet-700 dark:text-violet-400', icon: '🌟' },
};

const BADGE_ICONS = {
  'New Traveler': '🌱', 'First Flight': '🛫', 'Regular Flyer': '✈️',
  'Frequent Flyer': '🏆', 'Jet Setter': '🌍', 'Sky High': '🚀',
  'Community Star': '⭐', 'Fly N Out Club Member': '👑',
};

function StatCard({ icon: Icon, label, value, sub, color = '#FF4500', delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <Card className="fly-card p-4 h-full">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk', color }}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
            <Icon className="w-4.5 h-4.5" style={{ color }} />
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [likedPosts, setLikedPosts] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [flightNumber, setFlightNumber] = useState('');
  const [origin, setOrigin] = useState('');
  const [dest, setDest] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [user, loading, navigate]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      const [statsRes, likedRes] = await Promise.all([
        axios.get(`${API}/users/me/stats`, { withCredentials: true }),
        axios.get(`${API}/users/me/liked-posts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setLikedPosts(likedRes.data || []);
    } catch (err) {
      console.error('Stats fetch failed:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleCheckin = async () => {
    if (!flightNumber.trim()) { toast.error('Enter a flight number'); return; }
    setCheckingIn(true);
    try {
      const payload = { flight_number: flightNumber };
      if (origin.trim()) payload.origin_code = origin.trim().toUpperCase();
      if (dest.trim()) payload.destination_code = dest.trim().toUpperCase();
      const res = await axios.post(`${API}/flights/checkin`, payload, { withCredentials: true });
      toast.success(`Flight ${flightNumber.toUpperCase()} logged! +10 XP`);
      setLogDialogOpen(false);
      setFlightNumber(''); setOrigin(''); setDest('');
      // Refresh stats then navigate
      await fetchStats();
      navigate(`/route/${res.data.route_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const rankInfo = stats?.rank;
  const rankStyle = RANK_COLORS[rankInfo?.name] || RANK_COLORS['Boarding Pass'];
  const memberSince = stats?.member_since ? new Date(stats.member_since).getFullYear() : null;

  const flightTrend = (() => {
    if (!stats) return null;
    const curr = stats.flights_this_month;
    const prev = stats.flights_last_month;
    if (prev === 0 && curr === 0) return null;
    if (prev === 0) return '+100%';
    const pct = Math.round(((curr - prev) / prev) * 100);
    return pct >= 0 ? `+${pct}%` : `${pct}%`;
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Dashboard — My Flights & Analytics"
        description="Your personal Fly N Out dashboard. Track logged flights, XP progress, rank milestones, community posts, liked articles, and route exploration stats."
        url="/dashboard"
        noIndex={true}
      />
      <Navbar />

      {/* Hero Header */}
      <div className="fly-gradient-gate border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Avatar + greeting */}
            <div className="flex items-center gap-4 flex-1">
              <Avatar className="w-14 h-14 sm:w-16 sm:h-16 ring-2 ring-[#FF4500]/30">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-[#FF4500] text-white text-xl font-bold">
                  {user?.name?.[0] || 'T'}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                    {user?.name?.split(' ')[0] || 'Traveler'}
                  </h1>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${rankStyle.bg} ${rankStyle.text}`}>
                    {rankStyle.icon} {rankInfo?.name || 'Boarding Pass'}
                  </span>
                  {stats?.is_premium && (
                    <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-[10px]">
                      <Crown className="w-2.5 h-2.5 mr-1" /> Club Member
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {memberSince ? `Flying since ${memberSince}` : 'Welcome to Fly N Out'} ·{' '}
                  {statsLoading ? '—' : `Rank #${stats?.community_rank || '—'} in community`}
                </p>
              </div>
            </div>
            {/* XP Ring + Bar */}
            <div className="sm:w-72">
              {statsLoading ? (
                <Skeleton className="h-20 w-full rounded-xl" />
              ) : (
                <div className="bg-background/60 backdrop-blur-sm rounded-xl p-3 border border-border flex items-center gap-3">
                  <XPRing xp={stats?.xp || 0} size={56} strokeWidth={6} />
                  <div className="flex-1 min-w-0">
                    <RankBadge rankName={rankInfo?.name || 'Boarding Pass'} xp={stats?.xp || 0} size="sm" />
                    <div className="mt-1.5">
                      {rankInfo?.next_rank ? (
                        <p className="text-[10px] text-muted-foreground">
                          {rankInfo.next_rank.xp_needed - (stats?.xp || 0)} XP → {rankInfo.next_rank.icon} {rankInfo.next_rank.name}
                        </p>
                      ) : (
                        <p className="text-[10px] font-medium text-[#FF4500]">Max Rank! 🌟</p>
                      )}
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-muted/60 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: '#FF4500' }}
                        initial={{ width: 0 }}
                        animate={{ width: `${stats?.xp_progress || 0}%` }}
                        transition={{ duration: 1.2, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex-1">
        <div className="grid lg:grid-cols-3 gap-6">

          {/* ── Main Column ─────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {statsLoading ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)
              ) : (
                <>
                  <StatCard icon={Plane} label="Flights Logged" value={stats?.flight_count ?? 0}
                    sub={flightTrend ? `${flightTrend} this month` : 'Start flying!'} delay={0} />
                  <StatCard icon={Route} label="Routes Explored" value={stats?.routes_explored ?? 0}
                    sub="Unique routes" color="#10B981" delay={0.06} />
                  <StatCard icon={BarChart2} label="Community Posts" value={stats?.posts_count ?? 0}
                    sub="Tips & insights" color="#7C3AED" delay={0.12} />
                  <StatCard icon={Heart} label="Liked Articles" value={stats?.liked_posts_count ?? 0}
                    sub="Saved for later" color="#E11D48" delay={0.18} />
                </>
              )}
            </div>

            {/* Badges */}
            {!statsLoading && stats?.badges?.length > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                <Card className="fly-card p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-4 h-4 text-[#FF4500]" />
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>My Badges</h3>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {stats.badges.length} earned
                    </span>
                  </div>
                  <BadgeShowcase earnedBadges={stats.badges} showLocked={true} />
                </Card>
              </motion.div>
            )}

            {/* Recent Flights */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                  <CalendarDays className="w-4 h-4 text-[#FF4500]" /> Recent Flights
                </h2>
                <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="fly-btn-primary" data-testid="checkin-drawer-btn">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Log Flight
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Log a Flight</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2" data-testid="flight-checkin-form">
                      <p className="text-sm text-muted-foreground">Log your flight to earn XP and join the route community.</p>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Flight Number *</label>
                          <Input
                            placeholder="e.g. EK101, SV001, QR567"
                            value={flightNumber}
                            onChange={e => setFlightNumber(e.target.value.toUpperCase())}
                            className="text-base uppercase font-mono"
                            data-testid="flight-number-input"
                            onKeyDown={e => e.key === 'Enter' && handleCheckin()}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs font-medium text-muted-foreground mb-1 block">Origin (optional)</label>
                            <Input placeholder="DXB" value={origin} onChange={e => setOrigin(e.target.value.toUpperCase())} maxLength={3} className="uppercase font-mono" />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-muted-foreground mb-1 block">Destination (optional)</label>
                            <Input placeholder="LHR" value={dest} onChange={e => setDest(e.target.value.toUpperCase())} maxLength={3} className="uppercase font-mono" />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {['EK101', 'SV001', 'QR567', 'TK234', 'BA089'].map(fn => (
                          <button key={fn} onClick={() => setFlightNumber(fn)}
                            className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-[#FF4500]/10 hover:text-[#FF4500] transition-colors font-mono">
                            {fn}
                          </button>
                        ))}
                      </div>
                      <Button onClick={handleCheckin} disabled={checkingIn} className="fly-btn-primary w-full" data-testid="flight-checkin-submit-button">
                        {checkingIn ? 'Logging...' : 'Log & Join Route'}
                        <Plane className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {statsLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
                </div>
              ) : stats?.recent_flights?.length === 0 ? (
                <Card className="fly-card p-8 text-center" data-testid="no-flights-card">
                  <Plane className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="font-medium text-sm">No flights logged yet</p>
                  <p className="text-xs text-muted-foreground mt-1 mb-4">Log your first flight to start earning XP and join route communities</p>
                  <Button size="sm" className="fly-btn-primary" onClick={() => setLogDialogOpen(true)}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Log Your First Flight
                  </Button>
                </Card>
              ) : (
                <div className="space-y-2">
                  {stats.recent_flights.map((flight, i) => {
                    const routeId = `${flight.origin_code || 'UNK'}-${flight.destination_code || 'UNK'}`;
                    const flightDate = flight.flight_date ? new Date(flight.flight_date).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
                    return (
                      <motion.div key={flight.record_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                        <Card
                          className="fly-card p-3.5 hover:shadow-md transition-all cursor-pointer group"
                          onClick={() => navigate(`/route/${routeId}`)}
                          data-testid="my-flights-flight-card"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#FF4500]/10 flex items-center justify-center flex-shrink-0">
                              <Plane className="w-4 h-4 text-[#FF4500]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-sm font-mono">{flight.flight_number}</span>
                                <span className="text-xs text-muted-foreground">
                                  {flight.origin_code || '?'} → {flight.destination_code || '?'}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground">{flightDate}</p>
                            </div>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#FF4500] opacity-0 group-hover:opacity-100 transition-opacity">
                              Community <ArrowRight className="ml-1 w-3 h-3" />
                            </Button>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                  <Button variant="ghost" size="sm" className="w-full text-muted-foreground" asChild>
                    <Link to={`/profile/${user?.user_id}`}>View all flights</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* ── Sidebar ──────────────────────────────────────── */}
          <div className="space-y-4">

            {/* Rank Roadmap */}
            {!statsLoading && stats && (
              <Card className="fly-card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-4 h-4 text-[#FF4500]" />
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>Rank Progress</h3>
                </div>
                <RankRoadmap xp={stats.xp || 0} compact />
              </Card>
            )}

            {/* Weekly Challenges */}
            <ChallengeWidget onXpGained={() => fetchStats()} />

            {/* Daily Quiz */}
            <QuizWidget onXpGained={() => fetchStats()} />

            {/* Liked Articles */}
            {likedPosts.length > 0 && (
              <Card className="fly-card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>Liked Articles</h3>
                </div>
                <div className="space-y-2">
                  {likedPosts.slice(0, 3).map(post => (
                    <Link
                      key={post.post_id}
                      to={`/blog/${post.slug}`}
                      className="flex items-start gap-2 p-2 rounded-lg hover:bg-muted transition-colors group"
                      data-testid="liked-post-item"
                    >
                      {post.cover_image && (
                        <img src={post.cover_image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium line-clamp-2 group-hover:text-[#FF4500] transition-colors">{post.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{post.read_time} min read</p>
                      </div>
                    </Link>
                  ))}
                  {likedPosts.length > 3 && (
                    <Link to="/blog" className="text-xs text-[#FF4500] hover:underline block text-center pt-1">
                      View all liked articles →
                    </Link>
                  )}
                </div>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="fly-card p-4">
              <h3 className="font-semibold text-sm mb-3" style={{ fontFamily: 'Space Grotesk' }}>Quick Actions</h3>
              <div className="space-y-2">
                <Button className="fly-btn-primary w-full justify-start" size="sm" onClick={() => setLogDialogOpen(true)} data-testid="sidebar-log-flight">
                  <Plus className="w-4 h-4 mr-2" /> Log a Flight
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm" asChild>
                  <Link to="/discover">
                    <Globe className="w-4 h-4 mr-2" /> Discover Routes
                  </Link>
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm" asChild>
                  <Link to="/community">
                    <Users className="w-4 h-4 mr-2" /> Leaderboard
                  </Link>
                </Button>
                <Button variant="outline" className="w-full justify-start" size="sm" asChild>
                  <Link to={`/profile/${user?.user_id}`}>
                    <Star className="w-4 h-4 mr-2" /> My Profile
                  </Link>
                </Button>
              </div>
            </Card>

            {/* Trending Routes */}
            <Card className="fly-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#FF4500]" />
                <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>Trending Routes</h3>
              </div>
              {statsLoading ? (
                <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
              ) : stats?.trending_routes?.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No routes yet — be the first!</p>
              ) : (
                <div className="space-y-2">
                  {stats.trending_routes.map((route, i) => (
                    <Link key={route.route_id} to={`/route/${route.route_id}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors group"
                      data-testid="trending-route-item">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-muted-foreground w-5">{i + 1}.</span>
                        <div>
                          <span className="text-sm font-semibold font-mono">{route.route_id}</span>
                          <span className="text-[10px] text-muted-foreground ml-2">{route.checkin_count || 0} flyers</span>
                        </div>
                      </div>
                      <ArrowRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            {/* Upgrade prompt (non-premium) */}
            {!stats?.is_premium && !statsLoading && (
              <Card className="fly-card p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4 text-amber-600" />
                  <h3 className="font-semibold text-sm text-amber-800 dark:text-amber-300">Fly N Out Club</h3>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">Unlock exclusive badges, priority support, and premium route insights.</p>
                <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600 text-white border-0" asChild>
                  <Link to="/premium"><Crown className="w-3.5 h-3.5 mr-1" /> Upgrade Now</Link>
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
